import { useToastError } from '@/hooks';
import { CubeQuery } from '@/types/cube';
import { Error as ApiError } from '@/types/error';
import { sanitizeCubeQuery } from '@/utils/cube';
import useDeepMemo from '@/utils/useDeepMemo';
import useInvalidTokenHandler from '@/utils/useInvalidTokenHandler';
import { QueryRecordType, ResultSet } from '@cubejs-client/core';
import { CubeContext } from '@cubejs-client/react';
import { UseQueryOptions, UseQueryResult, useQuery as useTanStackQuery } from '@tanstack/react-query';
import { uniqueId } from 'lodash-es';
import { useCallback, useContext } from 'react';
import useAuthTokenManager from './useAuthTokenManager';
import { processDecomposedResults, transformResult } from './utils';

const ERROR_ID = uniqueId();

export type QueryResult<T = unknown> = ResultSet<QueryRecordType<T>> & {
  loadResponses?: Array<{ data: Array<{ compareDateRange?: string }>; total?: number }>;
};

function useQuery<TQueryFnData = unknown, TError = ApiError, TData = TQueryFnData>({
  payload,
  options,
  decompose,
  mergeDimension,
  preserveNulls = false,
  __unstableDisableSecurityContext: bypassSecurityContext = false,
}: {
  payload: CubeQuery;
  options?: UseQueryOptions<TQueryFnData, TError, TData>;
  decompose?: boolean;
  mergeDimension?: string;
  preserveNulls?: boolean;
  __unstableDisableSecurityContext?: boolean;
}): UseQueryResult<TData, TError> {
  const { cubejsApi } = useContext(CubeContext);
  const { isCheckingToken } = useAuthTokenManager();

  const query = useDeepMemo(sanitizeCubeQuery(payload, !bypassSecurityContext));

  const wrappedQueryFn = useCallback(async (): Promise<TQueryFnData> => {
    if (isCheckingToken) {
      throw new Error('Token is being checked');
    }

    const result = (await cubejsApi.load(query)) as QueryResult;

    if (decompose) {
      const decomposedResults = result.decompose() as QueryResult[];
      return processDecomposedResults(decomposedResults, mergeDimension, preserveNulls) as TQueryFnData;
    }

    if (query?.total) {
      const totalCount = result.loadResponses[0].total;
      return {
        totalCount,
        data: transformResult(result, preserveNulls),
      } as TQueryFnData;
    }

    return transformResult(result, preserveNulls) as TQueryFnData;
  }, [query, cubejsApi, decompose, isCheckingToken, mergeDimension, preserveNulls]);

  const queryResult = useTanStackQuery<TQueryFnData, TError, TData>({
    ...options,
    queryFn: wrappedQueryFn,
    enabled: options?.enabled && !isCheckingToken,
  });

  useToastError(queryResult.error as ApiError, { toastId: ERROR_ID });

  useInvalidTokenHandler(queryResult.error);

  return queryResult;
}

export default useQuery;
